/**
 * Spec №22.1 (D-CC2/D-CC3/D-CC4) — THE decision card. ONE component, FOUR homes:
 * the task cockpit (full variant, the HERO), the desk's Work plane, the Needs-You
 * home, and the matter DO-NEXT rail (compact variant). Grep-gated to a single
 * definition (D-CC4) — every surface imports THIS.
 *
 * A Needs-You escalation is STRUCTURED, not prose: a question (`reason`) → evidence
 * chips (each a fact, the 🧠 one a recalled memory) → option buttons that are PREPARED
 * ACTIONS (label + one-line consequence + a preview of what they produce). Choosing an
 * option calls answer_needs_you — it executes the option's snapshotted verb through the
 * one dispatch, stamped requested-by-you. The standing footer never leaves: "or just
 * type — telling the agent what to do IS answering" + Take over. Money is never an
 * option (D-CC8).
 *
 * №22.2 (D-CV1) — answering is an EVENT WITH A RECEIPT, never a disappearance. Once
 * answered, the SAME card transitions to its RESOLVED state (the `resolved` prop):
 *   - full (the thread record): chosen option highlighted with ✓, the others greyed,
 *     stamped "✓ {who} chose {label} · {time}", and INERT (a resolved card cannot be
 *     re-clicked — the "Invalid option" error is thereby retired).
 *   - compact (a list): collapses to a one-line receipt ("✓ {label} — {who}, {age} ·
 *     view thread →"). The surface fades it on next visit, never instantly.
 * Trust is built at exactly this moment; the 22.1 silent-vanish was the anti-demo.
 *
 * №22.5 (C1–C4) — the four upgrades, all landing HERE so every surface inherits them
 * (D-CC4; a second implementation anywhere is a red row):
 *   C1 BATCH + PAGER — several judgements from one run are ONE card with `‹ 2 of 4 ›`
 *      and "3 more after this". Answering advances the server-side cursor, so it
 *      advances on every surface at once.
 *   C2 EVIDENCE INSIDE THE QUESTION — the facts move INTO the card body, above the
 *      options, as a readable block rather than a row of chips beside the question.
 *      Refs stay linked where a route can be built (see `evidenceHref`).
 *   C3 PRE-FILL — where the record already answers, the option arrives SELECTED with
 *      its source shown ("pre-filled from §4.2"). That switches the card into
 *      select-then-Confirm, because a pre-fill must never confirm itself: the record
 *      proposes, the human commits. Cards with no pre-fill keep D-CC2's one-click
 *      buttons exactly as shipped.
 *   C4 SKIP RECORDS A CONSEQUENCE — skipping is offered, and states what it leaves
 *      behind, before it is pressed. Marks from earlier skips stay on the card.
 *
 * Token-only, theme-aware, scoped to `.appshell`. The mockup palette maps to the alias
 * tokens (--accent teal · --warn amber · --danger red) + the purple ramp for the memory
 * provenance chip (--c-purple-600) — no new CSS system (R1).
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router";
import type { NeedsYouOption, NeedsYouEvidence, NeedsYouBatchPosition, SkippedMark } from "../../../lib/types";
import { decisionCardMode, prefilledIndex, pagerLabel, remainderLine, skipLine, evidenceHref } from "./decision-card-model";

export interface DecisionCardContext {
  agentName?: string;
  agentInitials?: string;
  agentColor?: string;
  /** "Patel — settlement · Exchange" — the anchor + where-it-sits, one line. */
  anchorLabel?: string;
  age?: string;
}

export interface DecisionCardProps {
  variant?: "full" | "compact";
  /** The question — the card's headline. */
  reason: string;
  evidence?: NeedsYouEvidence[];
  options?: NeedsYouOption[];
  context?: DecisionCardContext;
  /** Choose a prepared option (executes its snapshot). Index into `options`. */
  onChoose?: (optionIndex: number) => void;
  choosing?: boolean;
  chosenIndex?: number | null;
  /** Focus the composer (full) — "typing is answering". */
  onAssist?: () => void;
  onTakeOver?: () => void;
  takingOver?: boolean;
  /** The task's canonical deep-link URL (`/app/task/:id`) — kept for reference and
   *  the "Open full →" cockpit route; the in-loop affordance uses `onOpenThread`. */
  openThreadHref?: string;
  /**
   * №22.3 (D-CH4) — the thread affordance ("view thread" / "work through" / "open
   * task") retargets to the docked PANEL (focus + scroll-to), never a route. It
   * renders ONLY when this is set — a button that calls it client-side, zero reload
   * (the D-CH3 invariant). No `<a href>` fallback exists.
   */
  onOpenThread?: () => void;
  error?: string | null;
  /**
   * №22.2 (D-CV1) — when set, the card is RESOLVED (answered). It renders inert with
   * the chosen option highlighted + a receipt stamp; compact collapses to a one-liner.
   * №22.5 (C4) adds the `skipped` outcome — ⊘, with the consequence it recorded.
   */
  resolved?: {
    optionIndex: number | null;
    label?: string | null;
    at?: string | null;
    byName?: string | null;
    outcome?: "executed" | "failed" | "skipped";
    consequence?: string | null;
  } | null;
  /** №22.5 (C1) — this question's position in its run's batch. Total is always ≥ 2. */
  batch?: NeedsYouBatchPosition | null;
  /**
   * №22.5 (F7) — WHY AM I BEING ASKED THIS? David, on the walk: *"I'm looking at the
   * card, and I don't know WHY it's here."*
   *
   * The card named who asked and what record it sits on, and never what put the work
   * there — so a workflow step, a routine, a forwarded email and a task someone typed
   * out of a chat all arrived looking identical. The line is already derived
   * server-side (22.4.1 F3, `deriveTaskProvenance`) and already rendered on the desk;
   * it simply never reached the card. One line, existing data, nothing invented.
   */
  provenance?: { label?: string | null } | null;
  /** №22.5 (C4) — what skipping records, authored at raise-time. Absent → the generic line. */
  skipConsequence?: string | null;
  /** №22.5 (C4) — skip this question. Renders the Skip affordance ONLY when supplied. */
  onSkip?: () => void;
  skipping?: boolean;
  /** №22.5 (C4) — marks left by questions already skipped on this task. */
  skipped?: SkippedMark[];
  /**
   * №53 (53.1) — render the 1/2/3 key badge on each option.
   *
   * OPT-IN, not always-on, and the reason is the whole point of the badge: the
   * digits are bound by whichever surface OWNS the keyboard, and only the Needs-you
   * stage does. The same card renders inside the docked chat panel, where a digit
   * belongs to the composer — a badge there would advertise a binding that does not
   * exist, which is worse than no badge at all.
   */
  showOptionKeys?: boolean;
  /**
   * №22.5 (F10) — ANSWER IN YOUR OWN WORDS, from inside the card.
   *
   * The locked mockup D ends its option list with "Something else…", and the original
   * transfer dropped it. That left D-CC3's *"free text ALWAYS beats buttons"* true only
   * where a composer happens to exist — the thread — while the desk, the Needs-You home
   * and the matter rail offered a menu and a link to go somewhere else. A menu you
   * cannot argue with is not a decision surface.
   *
   * Wired to the same path typing in the thread uses, so the words reach the hire WITH
   * the question they answer (F6) and the batch advances rather than resetting (F5).
   * Absent → the card renders no free-text row, and the surface is responsible for
   * saying why (the optionless fallback still points at the thread).
   */
  onReply?: (text: string) => void;
  replying?: boolean;
  "data-testid"?: string;
}

const EVIDENCE_GLYPH: Record<string, string> = { memory: "🧠", key_date: "⚑" };

/** "just now" / "2m ago" / "3h ago" / "2d ago" — the receipt's age (D-CV1). Pure-ish. */
function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * №22.3 (D-CH4) — the ONE thread affordance, panel-first and panel-only. A text
 * button that focuses the docked panel (`onOpenThread`); there is deliberately NO
 * `<a href>` fallback — a raw anchor would full-reload the document, which is exactly
 * the invariant D-CH3 forbids. Surfaces without a panel simply don't render it.
 * Module-level so it isn't re-created per render.
 */
function ThreadAffordance({
  onOpenThread, style, children, testid,
}: {
  onOpenThread?: () => void;
  style: CSSProperties;
  children: ReactNode;
  testid?: string;
}) {
  if (!onOpenThread) return null;
  return (
    <button type="button" style={{ ...linkButtonReset, ...style }} onClick={onOpenThread} data-testid={testid}>
      {children}
    </button>
  );
}

export function DecisionCard(props: DecisionCardProps) {
  const {
    variant = "full", reason, evidence = [], options = [], context,
    onChoose, choosing, chosenIndex, onAssist, onTakeOver, takingOver, onOpenThread, error, resolved,
    batch, skipConsequence, onSkip, skipping, skipped = [], provenance, onReply, replying, showOptionKeys,
  } = props;
  // F7 — "from conveyancing-purchase-demo › Open & instructions". Absent when the
  // server did not derive one, rather than a guessed "manual" (R7).
  const whyLine = provenance?.label?.trim() || null;
  const compact = variant === "compact";
  const hasOptions = options.length > 0;
  // №22.4 — client-side routing for a maker-link option (see the option buttons).
  const navigate = useNavigate();

  // №22.5 (C3) — the mode is DATA, not a prop: a card is select-then-Confirm exactly
  // when the record pre-filled one of its options. Derived through the mirrored model
  // module so the renderer and the server cannot drift on what a pre-fill is.
  const preIndex = prefilledIndex(options);
  const selectMode = decisionCardMode(options) === "select";
  // The pre-filled option is the INITIAL selection and nothing more — there is
  // deliberately no effect, timer or auto-submit anywhere in this component that turns
  // a selection into an answer. The human presses Confirm. (C3: a pre-fill NEVER
  // auto-confirms; the R5 gate greps this file for exactly that.)
  const [selected, setSelected] = useState<number | null>(preIndex);
  // F10 — the free-text row. `otherOpen` is BOTH the selection (in select mode) and the
  // disclosure (in direct mode), because they are the same act: choosing to say
  // something the menu did not offer.
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState("");
  const canReply = !!onReply;
  const busy = !!choosing || !!skipping || !!replying;
  const sendReply = () => { const t = otherText.trim(); if (t) onReply?.(t); };
  /**
   * №53 (53.1) — 1/2/3, BOUND WHERE THE OPTIONS ARE.
   *
   * The first cut bound these on the Needs-you page and called `onChoose` directly.
   * That was wrong in a way worth recording: Henderson's card is PRE-FILLED, so it
   * renders in §22.5 C3's select mode — radio rows plus Confirm, because "a
   * pre-filled card commits only when the human presses Confirm". A digit that
   * committed anyway would have made the keyboard path bypass a ruling the mouse
   * path obeys, and the two would disagree about what the card is for.
   *
   * A shortcut is an ACCELERATOR FOR THE VISIBLE AFFORDANCE, never a second, faster
   * set of semantics. So the digit does exactly what clicking that row does:
   * selects in select mode, commits in direct mode. The card knows its mode; the
   * page does not, which is why the binding belongs here.
   *
   * Guards: a modifier (⌘1 is the browser's tab switch), a typing target (the
   * ask-first textarea sits directly under these options), a busy card, and a
   * resolved one — a keystroke must never re-answer a receipt.
   */
  useEffect(() => {
    if (!showOptionKeys || resolved || !hasOptions) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const el = ev.target as HTMLElement | null;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.isContentEditable) return;
      if (!/^[1-9]$/.test(ev.key)) return;
      const i = Number(ev.key) - 1;
      if (i >= options.length || busy) return;
      ev.preventDefault();
      const o = options[i]!;
      if (!o.prepared_call && typeof o.href === "string" && o.href.startsWith("/")) { navigate(o.href); return; }
      if (selectMode) { setSelected(i); setOtherOpen(false); } else onChoose?.(i);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showOptionKeys, resolved, hasOptions, options, busy, selectMode, onChoose, navigate]);


  // №22.5 (C1) — the pager and the "n more after this" line, from the batch position
  // the server serves. Both are null for an unbatched card, and nothing renders.
  const pager = batch ? pagerLabel(batch.index, batch.total) : null;
  const remainder = batch ? remainderLine(batch.index, batch.total) : null;

  // The thread affordance renders only when we can focus the panel (D-CH4) — never
  // a raw link. `openThreadHref` stays on the props as the canonical deep-link URL.
  const hasOpenThread = !!onOpenThread;

  // ── №22.2 (D-CV1) — RESOLVED. The receipt, in place of the vanish. ──────────
  if (resolved) {
    const who = resolved.byName ?? "you";
    // №22.5 (C4) — a skip has no chosen option, so it is labelled by what it IS.
    const wasSkipped = resolved.outcome === "skipped";
    const chosenLabel = wasSkipped
      ? "Skipped"
      : resolved.label
        ?? (resolved.optionIndex != null ? options[resolved.optionIndex]?.label : undefined)
        ?? "your choice";
    const age = relTime(resolved.at);
    const failed = resolved.outcome === "failed";
    const mark = wasSkipped ? "⊘" : failed ? "✗" : "✓";
    const markColor = wasSkipped ? "var(--warn)" : failed ? "var(--danger)" : "var(--accent)";

    // compact — a one-line receipt (D-CV1). Fades on the surface's next visit.
    if (compact) {
      return (
        <div style={receiptRow} data-testid={props["data-testid"] ?? "decision-card"} data-variant="compact" data-resolved="true" data-outcome={resolved.outcome ?? "executed"}>
          <span style={{ ...receiptCheck, color: markColor }}>{mark}</span>
          <span style={receiptText}>
            <b style={{ color: "var(--text-1)", fontWeight: 600 }}>{chosenLabel}</b>
            <span style={{ color: "var(--text-3)" }}> — {who}{age ? `, ${age}` : ""}</span>
          </span>
          <ThreadAffordance onOpenThread={onOpenThread} style={receiptLink} testid="view-thread">view thread →</ThreadAffordance>
        </div>
      );
    }

    // full — the resolved card, in place: chosen highlighted ✓, the rest greyed, inert.
    return (
      <div style={cardResolved} data-testid={props["data-testid"] ?? "decision-card"} data-variant="full" data-resolved="true" data-outcome={resolved.outcome ?? "executed"}>
        <div style={head}>
          {/* `border` (shorthand) is rebuilt rather than overridden with `borderColor`:
              mixing the two makes React warn on re-render, which the skipped outcome
              made routine because it re-renders the pill in a third colour. */}
          <span style={{ ...pill, color: markColor, border: "1px solid var(--border)" }}>
            {wasSkipped ? "⊘ Skipped" : failed ? "✗ Not completed" : "✓ Resolved"}
          </span>
          {context && (context.agentName || context.anchorLabel) && (
            <span style={ctx}>
              {context.agentName && <b style={{ color: "var(--text-2)" }}>{context.agentName}</b>}
              {context.anchorLabel && <span>{context.agentName ? " on " : ""}<b style={{ color: "var(--text-2)" }}>{context.anchorLabel}</b></span>}
            </span>
          )}
          <span style={age ? { ...ageStamp } : { display: "none" }}>{age}</span>
        </div>
        <div style={{ ...question, color: "var(--text-2)" }}>{reason}</div>
        {hasOptions && (
          <div style={opts}>
            {options.map((o, i) => {
              const isChosen = i === resolved.optionIndex;
              return (
                <div key={i} style={isChosen ? (failed ? optChosenFailed : optChosen) : optGreyed} aria-disabled data-testid={`decision-option-${i}`}>
                  <span>{isChosen ? `${failed ? "✗" : "✓"} ` : ""}{o.label}</span>
                </div>
              );
            })}
          </div>
        )}
        {/* №22.5 (C4) — a skip's consequence is part of its receipt. The mark it left
            is stated on the record of the skip itself, not only at the moment of
            pressing: "I skipped that" is only half a record without "and this is what
            that left standing". */}
        {wasSkipped && resolved.consequence && (
          <div style={skipMarkLine} data-testid="skip-consequence">⚑ {resolved.consequence}</div>
        )}
        <div style={stampRow}>
          <span style={{ color: markColor, fontWeight: 700 }}>{mark}</span>
          {/* the chosen option is highlighted above; the stamp carries WHO + WHEN so it
              doesn't repeat the label (the thread's consequence chain states the choice). */}
          <span>
            {wasSkipped ? `${who} skipped this` : failed ? `${chosenLabel} couldn’t be completed` : `${who} answered`}
            {age ? ` · ${age}` : ""}
          </span>
          <ThreadAffordance onOpenThread={onOpenThread} style={{ ...receiptLink, marginLeft: "auto" }} testid="view-thread">view thread →</ThreadAffordance>
        </div>
      </div>
    );
  }

  return (
    <div style={compact ? cardCompact : cardFull} data-testid={props["data-testid"] ?? "decision-card"} data-variant={variant}>
      {/* №53 — crew's `.from`: ONE line, 11.5px, muted, the hire's name in the accent.
       *
       *   "Raised by **Matter Runner** · Henderson v Coastline Property"
       *
       * What this replaces: a `◑ Needs you` pill, an initials avatar chip and an
       * "on <matter>" clause — three pieces of chrome saying what one sentence says.
       * The pill in particular labelled the card with the name of the page it was
       * already on. The prototype spends nothing here because the question below is
       * what the reader came for, and every gram above it is weight the question has
       * to carry. Audit §5 ①. */}
      <div style={head}>
        {context && (context.agentName || context.anchorLabel) && (
          <span style={fromLine}>
            {context.agentName && <>Raised by <b style={{ color: "var(--accent)", fontWeight: 600 }}>{context.agentName}</b></>}
            {context.anchorLabel && <>{context.agentName ? " · " : ""}{context.anchorLabel}</>}
          </span>
        )}
        {/* №22.5 (C1) — the pager. Sits in the head beside who is asking, because
            "which of how many" is part of identifying the question, not a footer note. */}
        {pager && <span style={pagerPill} data-testid="decision-pager">{pager}</span>}
        {!compact && !context?.age && !showOptionKeys && <span style={pausedNote}>paused · waiting on your call</span>}
        {/* №53 — not on the stage. The rail row immediately to the left already says
         * "Matter Runner · 08:51 · due today"; repeating it in the card head put the
         * same sentence on screen twice, a few centimetres apart. Crew's card carries
         * no stamp at all — the queue owns "when", the card owns "what". */}
        {context?.age && !showOptionKeys && <span style={age}>{context.age}</span>}
      </div>

      {/* F7 — why this is on your desk at all. Above the question, quiet, one line:
          it is the frame you read the question inside, not part of the question. */}
      {whyLine && <div style={whyRow} data-testid="decision-why">{whyLine}</div>}

      {/* the question */}
      <div style={compact ? questionCompact : question}>{reason}</div>

      {/* №22.5 (C2) — THE EVIDENCE, INSIDE THE QUESTION.
          It used to be a row of small chips: the labels were there, but a chip row
          reads as decoration beside the question rather than as the case FOR it, and
          the `ref` each chip carried was never rendered at all — so "each chip is a
          link" was true of the payload and false of the pixels. Now the facts are a
          block in the card body, ahead of the options, one per line, each carrying its
          own provenance: a link where a route can be built, the ref itself where it
          cannot (never a link that 404s). */}
      {evidence.length > 0 && (
        <div style={eviBlock} data-testid="decision-evidence">
          {evidence.map((e, i) => {
            const href = evidenceHref(e.ref);
            const glyph = EVIDENCE_GLYPH[e.kind ?? ""] ?? "";
            const tone = e.kind === "memory" ? "var(--c-purple-600)" : e.kind === "key_date" ? "var(--warn)" : "var(--text-2)";
            return (
              <div key={i} style={eviLine}>
                {glyph && <span style={{ ...eviGlyph, color: tone }}>{glyph}</span>}
                <span style={eviText}>
                  {href
                    ? <button type="button" style={{ ...linkButtonReset, ...eviLink }} onClick={() => navigate(href)} data-testid={`evidence-link-${i}`}>{e.label}</button>
                    : e.label}
                  {!href && e.ref && <span style={eviRef}> · {e.ref}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* №53 — crew's `.prep`, "ALREADY PREPARED", and its POSITION is the point.
       *
       * It rendered last, below Confirm and Skip, labelled "PREVIEW — WHAT <option>
       * SENDS". Two things wrong with that. The label framed prepared work as a
       * preview of something that might happen; the card's whole argument is that
       * the work is ALREADY DONE and the only thing outstanding is the judgement.
       * And the position put it after the commit, where a reader deciding whether to
       * commit has already gone past it. Crew places it between the question and the
       * options, which is where it is read. Audit §5 ⑤. */}
      {!compact && hasOptions && options[0]?.preview_artifact && (
        <div style={preview}>
          <div style={previewLabel}>Already prepared</div>
          {options[0].preview_artifact}
        </div>
      )}

      {/* option buttons — prepared actions (D-CC2), or (C3) radio rows + Confirm when
          the record pre-filled one of them. */}
      {hasOptions && (
        <div style={selectMode ? optRows : opts}>
          {options.map((o, i) => {
            // №22.4 (D-HM1 ③) — a MAKER-LINK option (the blocked-honest "equip me")
            // has nowhere to execute and somewhere to GO. It navigates client-side;
            // a raw `<a href>` here would full-reload the loop (D-CH3).
            const isLink = !o.prepared_call && typeof o.href === "string" && o.href.startsWith("/");
            const source = o.prefilled && o.prefilled_from ? o.prefilled_from : null;

            // ── C3 select mode — the record proposes, the human commits ──
            if (selectMode) {
              const isSel = selected === i;
              return (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={isSel}
                  style={isSel ? optRowSel : optRow}
                  disabled={busy}
                  onClick={() => (isLink ? navigate(o.href!) : (setSelected(i), setOtherOpen(false)))}
                  data-testid={`decision-option-${i}`}
                  data-selected={isSel ? "true" : "false"}
                >
                  <span style={{ ...radioDot, ...(isSel ? radioDotOn : null) }} aria-hidden="true" />
                  {/* №53 — crew's `.opt` body: **label and consequence on ONE line**,
                   *   <b>Counter at $400 with the make-good</b> <small>— sends the
                   *   drafted reply, briefs the client, captures 0.3h to you</small>
                   * with the pre-fill provenance beneath. We stacked all three, which
                   * turned a three-option card into a wall and lost the sentence the
                   * drawing actually reads as. The em-dash is crew's, not decoration:
                   * it is what makes the consequence a continuation of the label
                   * rather than a second fact about it. Audit §5 ⑦. */}
                  <span style={optRowBody}>
                    <span style={optRowLabel}>
                      <b style={{ fontWeight: 600 }}>{o.label}</b>
                      {!compact && o.consequence && <small style={optConseq}>— {o.consequence}</small>}
                    </span>
                    {source && <span style={prefillTag} data-testid={`prefill-source-${i}`}>{source}</span>}
                  </span>
                  {showOptionKeys && i < 9 && <span className="nykey" aria-hidden="true">{i + 1}</span>}
                </button>
              );
            }

            // ── D-CC2 direct mode — the click IS the commit, unchanged ──
            return (
              <button
                key={i}
                type="button"
                style={i === 0 ? optPrimary : optAlt}
                disabled={busy}
                onClick={() => (isLink ? navigate(o.href!) : onChoose?.(i))}
                data-testid={`decision-option-${i}`}
              >
                {/* №53 · E-28 — THE PROVENANCE RENDERS IN DIRECT MODE TOO.
                 *
                 * It used to appear only in select mode, which was harmless while a
                 * pre-fill FORCED select mode. Narrowing the commit grammar broke
                 * that coupling: a pre-filled card whose options are all local is
                 * now direct, and the pre-fill lost the line saying where it came
                 * from. That is precisely the claim the ruling rests on — C3's real
                 * requirement is that a pre-filled answer is visible as a PROPOSAL
                 * before it becomes an act, and it is the provenance line and the
                 * tint that carry it once the extra press is gone. `card-trace-22.5`
                 * caught it. */}
                <span style={optRowBody}>
                  <span style={optRowLabel}>
                    <b style={{ fontWeight: 600 }}>{choosing && chosenIndex === i ? "Working…" : o.label}</b>
                    {!compact && o.consequence && <small style={optConseq}>— {o.consequence}</small>}
                  </span>
                  {source && <span style={prefillTag} data-testid={`prefill-source-${i}`}>{source}</span>}
                </span>
                {/* 53.1 — the digit the Needs-you stage binds. `aria-hidden` because
                    it is a hint about a shortcut, not part of the option's name; the
                    button already reads out its label and its consequence. */}
                {showOptionKeys && i < 9 && <span className="nykey" aria-hidden="true">{i + 1}</span>}
              </button>
            );
          })}

          {/* F10 — "Something else…" (mockup D's fourth row, verbatim). ALWAYS chrome,
              never authored data: if a raiser could omit it, a card could trap a human
              inside a menu, which is precisely what "free text always beats buttons"
              forbids. It is the last row because it is the answer for when none of the
              answers above are the answer. */}
          {/* №53 — on the STAGE the ask box is always open (crew's `.ask`, rendered
           * below), so the "Something else…" row that used to reveal it would be a
           * control for opening something already open. It stays on every other
           * variant, where the box is still hidden until asked for. Audit §5 ⑧. */}
          {canReply && !showOptionKeys && (
            selectMode ? (
              <button
                type="button"
                role="radio"
                aria-checked={otherOpen}
                style={otherOpen ? optRowSel : optRow}
                disabled={busy}
                onClick={() => { setOtherOpen(true); setSelected(null); }}
                data-testid="decision-other"
                data-selected={otherOpen ? "true" : "false"}
              >
                <span style={{ ...radioDot, ...(otherOpen ? radioDotOn : null) }} aria-hidden="true" />
                <span style={optRowBody}><span style={optRowLabel}>Something else…</span></span>
              </button>
            ) : (
              <button
                type="button"
                style={optAlt}
                disabled={busy}
                onClick={() => setOtherOpen((v) => !v)}
                data-testid="decision-other"
              >
                <span>{hasOptions ? "Something else…" : "Answer in your own words…"}</span>
              </button>
            )
          )}
        </div>
      )}

      {/* F10 — the box. Rendered only once asked for, so a card at rest still reads as
          a question with answers rather than a form. ⌘/Ctrl+Enter sends, because the
          plain Enter of a multi-line answer is a newline. */}
      {canReply && (otherOpen || showOptionKeys) && (
        /* №53 — crew's `.ask`: on the stage it is a ROW, one line plus an Ask
         * button, standing open beneath the options exactly as the drawing shows
         * it. Elsewhere it stays the reveal-on-request textarea F10 designed, so
         * a compact card at rest still reads as a question with answers rather
         * than as a form. Enter sends on the one-line form (there is no newline to
         * lose); ⌘/Ctrl+Enter still sends on the textarea. */
        <div style={showOptionKeys ? askRow : otherWrap}>
          <textarea
            autoFocus={otherOpen}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              if (showOptionKeys ? !e.shiftKey : (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendReply(); }
            }}
            placeholder={showOptionKeys ? "Ask the runner something first… e.g. what did we settle at last time?" : "Tell them what to do instead…"}
            rows={showOptionKeys ? 1 : compact ? 2 : 3}
            style={showOptionKeys ? { ...otherInput, resize: "none", flex: 1, height: 33, minHeight: 33, overflow: "hidden" } : otherInput}
            data-testid="decision-other-input"
          />
          {showOptionKeys && (
            <button type="button" style={skipBtn} disabled={!otherText.trim() || busy} onClick={sendReply} data-testid="decision-ask">
              {replying ? "Asking…" : "Ask"}
            </button>
          )}
        </div>
      )}

      {/* The commit row (C3/C4/C1). Confirm appears ONLY in select mode — in direct
          mode the option button already committed and a second Confirm would ask the
          same question twice. Skip and the batch remainder are mode-independent. */}
      {(selectMode || onSkip || remainder || otherOpen) && (
        <div style={confirmRow}>
          {/* F10 — when the human is writing, the primary control SENDS their words.
              One button, whichever grammar the card is in: what they typed is the
              answer, and it must not need a second decision about how to submit it. */}
          {otherOpen ? (
            <button
              type="button"
              style={confirmBtn}
              disabled={!otherText.trim() || busy}
              onClick={sendReply}
              data-testid="decision-send-reply"
            >
              {replying ? "Sending…" : "Send"}
            </button>
          ) : selectMode && hasOptions ? (
            <button
              type="button"
              style={confirmBtn}
              disabled={selected == null || busy}
              onClick={() => { if (selected != null) onChoose?.(selected); }}
              data-testid="decision-confirm"
            >
              {choosing ? "Working…" : "Confirm"}
            </button>
          ) : null}
          {onSkip && (
            <button type="button" style={skipBtn} disabled={choosing || skipping} onClick={onSkip} data-testid="decision-skip">
              {skipping ? "Skipping…" : "Skip"}
            </button>
          )}
          {/* C4 — the consequence is stated BEFORE the button is pressed. A skip whose
              cost you learn afterwards is not a choice, it is a surprise. */}
          {onSkip && <span style={skipNote} data-testid="skip-consequence">{skipLine(skipConsequence, context?.anchorLabel)}</span>}
          {remainder && <span style={remainderNote} data-testid="batch-remainder">{remainder}</span>}
        </div>
      )}

      {/* №22.2 (D-CV5) — NO optionless dead-ends. When the agent can't propose
          options, the card still carries the default action: "Work through it with me →"
          (opens the thread with the composer focused). Every card has ≥1 button, always. */}
      {!hasOptions && (
        <div style={opts}>
          {/* F10 — an optionless card can now be ANSWERED here rather than only
              followed elsewhere. The thread affordance stays beside it: "work through
              it with me" is a different intent from "here is my answer". */}
          {canReply && (
            <button type="button" style={optPrimary} disabled={busy} onClick={() => setOtherOpen((v) => !v)} data-testid="decision-other">
              Answer in your own words…
            </button>
          )}
          {compact
            ? (hasOpenThread
                ? <ThreadAffordance onOpenThread={onOpenThread} style={{ ...(canReply ? optAlt : optPrimary), textDecoration: "none" }} testid="work-through">Work through it with me →</ThreadAffordance>
                : (!canReply && <span style={{ ...optAlt, cursor: "default" }}>Reply in the thread — typing is answering</span>))
            : <button type="button" style={canReply ? optAlt : optPrimary} onClick={onAssist} data-testid="work-through">Work through it with me →</button>}
        </div>
      )}

      {/* footer — free text always answers + take over */}
      {compact ? (
        hasOptions && hasOpenThread ? (
          <div style={moreRow}><ThreadAffordance onOpenThread={onOpenThread} style={moreLink} testid="open-task">preview + full thread → open task</ThreadAffordance></div>
        ) : null
      ) : (
        <div style={foot}>
          {/* D-L6 (№22.5 ride-along) — "telling the agent what to do" was the locked
              22.1 footer. They are staff, so it is "them": the sentence is about a
              colleague you are speaking to, which is the whole point of the ruling. */}
          <span>↳ or just type below — telling them what to do <b style={{ color: "var(--text-1)" }}>is</b> answering</span>
          {/* №53 (53.1) — the thread affordance belongs on the FULL card too.
              It rendered only on `compact`, which was harmless while every Needs-you
              card was compact; the two-pane made the stage card `full` and the ONLY
              route from a decision into its docked thread disappeared with it. Two
              specs caught it (`panel-entry-options`), which is the argument for
              re-pointing rather than deleting the assertions of a surface you change:
              they were not testing the old layout, they were testing D-CH4's
              reachability, and that claim is still true and was briefly false. */}
          {hasOpenThread && (
            <>
              <span style={{ color: "var(--border-strong)" }}>|</span>
              <ThreadAffordance onOpenThread={onOpenThread} style={moreLink} testid="open-task">preview + full thread → open task</ThreadAffordance>
            </>
          )}
          {onTakeOver && (
            <>
              <span style={{ color: "var(--border-strong)" }}>|</span>
              <button type="button" style={takeOverBtn} onClick={onTakeOver} disabled={takingOver} data-testid="take-over">
                {takingOver ? "Taking over…" : "Take over this task"}
              </button>
            </>
          )}
        </div>
      )}

      {/* №22.5 (C4) — the marks earlier skips left on this task. They ride the card, so
          they appear wherever the card appears (D-CC4) and outlive the question that
          produced them: a skipped judgement is still owed after the rest is answered.
          Absent when there are none — never an empty "0 skipped" row (R7). */}
      {skipped.length > 0 && (
        <div style={skipMarksBlock} data-testid="skipped-marks">
          {skipped.map((m, i) => (
            <div key={i} style={skipMarkLine}>⚑ {m.consequence}</div>
          ))}
        </div>
      )}

      {error && <div style={errLine}>{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// styles (token-only, theme-aware, `.appshell` alias tokens)
// ---------------------------------------------------------------------------
const warnLine = "color-mix(in srgb, var(--warn) 34%, transparent)";

const cardFull: CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface-1)", borderRadius: 10,
  padding: "16px 18px",
};
const cardCompact: CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface-1)", borderRadius: 10, padding: "12px 14px",
};
const head: CSSProperties = { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" };
const pill: CSSProperties = {
  background: "var(--surface-1)", border: `1px solid ${warnLine}`, color: "var(--warn)",
  fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 9, whiteSpace: "nowrap",
};
const ctx: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--text-2)" };
/* crew `.card .from` — the whole header, in one 11.5px line. */
const fromLine: CSSProperties = { fontSize: 11.5, color: "var(--text-3)", lineHeight: 1.45 };
const pausedNote: CSSProperties = { marginLeft: "auto", fontSize: 11, color: "var(--warn)" };
const age: CSSProperties = { marginLeft: "auto", fontSize: 11, color: "var(--text-3)" };
const question: CSSProperties = { fontSize: 16.5, fontWeight: 600, color: "var(--text-1)", padding: "0 0 8px", lineHeight: 1.3 };
const questionCompact: CSSProperties = { fontSize: 13.5, fontWeight: 600, color: "var(--text-1)", padding: "0 0 6px", lineHeight: 1.3 };
// №22.5 (C2) — the evidence BLOCK: the case for the question, inside the question.
const eviBlock: CSSProperties = {
  display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, padding: 0, background: "none", border: 0,
};
/**
 * №53 (53.1.1) — A CHIP WRAPS; IT NEVER TRUNCATES.
 *
 * This carried `whiteSpace: nowrap` + `overflow: hidden` + `textOverflow: ellipsis`,
 * which is right for a short fact ("ceiling $400/m² · instructions 4 Aug") and wrong
 * for anything longer: David's 2026-08-20 Needs-you screenshots show two evidence
 * chips cut mid-word at the card's edge — "…does not appear in my queued task" — with
 * no affordance to read the rest. **A fact the reader cannot finish reading is not
 * evidence.** The blocked-honest guard now routes the model's own prose here
 * deliberately (see `isCapabilityName`), so this row has to hold a sentence well.
 *
 * Same rule D-CR26 already made for the Surfaces claim lines — *claims wrap, never
 * truncate* — applied to the other place the product shows a derived fact.
 */
const eviLine: CSSProperties = {
  display: "inline-flex", gap: 5, alignItems: "baseline", fontSize: 11,
  fontFamily: "var(--font-mono)", lineHeight: 1.5, background: "var(--surface-1)",
  border: "1px solid var(--border)", borderRadius: 6, padding: "3px 8px",
  maxWidth: "100%", minWidth: 0, overflowWrap: "anywhere",
};
const eviGlyph: CSSProperties = { flex: "0 0 auto", fontSize: 11 };
const eviText: CSSProperties = { color: "var(--text-2)", minWidth: 0 };
const eviLink: CSSProperties = { color: "var(--accent)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 2 };
const eviRef: CSSProperties = { color: "var(--text-3)", fontSize: 11 };
// №22.5 (F7) — the provenance line. Deliberately the quietest text on the card: it
// answers "why me, why now" without competing with the question it frames.
const whyRow: CSSProperties = {
  fontSize: 11, color: "var(--text-3)", lineHeight: 1.4, paddingBottom: 3,
};
// №22.5 (C1) — the pager pill.
const pagerPill: CSSProperties = {
  fontSize: 11, fontWeight: 600, color: "var(--text-2)", background: "var(--surface-1)",
  border: "1px solid var(--border)", borderRadius: 8, padding: "1px 8px",
  fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
};
// №22.5 (C3) — the select-mode option rows (mockup A/D's radio grammar).
const optRows: CSSProperties = { display: "grid", gap: 7, paddingBottom: 7 };
const optRow: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", textAlign: "left",
  width: "100%", border: "1px solid var(--border-strong)", background: "var(--surface-1)",
  borderRadius: 8, padding: "9px 11px", cursor: "pointer", color: "var(--text-1)",
  fontSize: 12.5, fontWeight: 400,
};
/* crew `.opt.rec` — the recommended/selected row: accent edge over the palest teal. */
const optRowSel: CSSProperties = {
  ...optRow,
  border: "1px solid var(--accent)", background: "var(--accent-tint)",
};
/* №53 — crew has NO radio dot: an option row is a button whose selected state is
 * the `.rec` edge and tint. The dot was our grammar, not the drawing's, and it put
 * a form control in the middle of a page that has none anywhere else. Kept as an
 * empty rule rather than deleted so the call sites stay legible. */
const radioDot: CSSProperties = { display: "none" };
const radioDotOn: CSSProperties = {};
const optRowBody: CSSProperties = { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 };
const optRowLabel: CSSProperties = { display: "block", minWidth: 0 };
const prefillTag: CSSProperties = {
  display: "inline-block", alignSelf: "flex-start", marginTop: 3,
  fontSize: 10, fontWeight: 500, letterSpacing: "0.02em", color: "var(--accent)",
  background: "color-mix(in srgb, var(--accent) 12%, var(--surface-1))",
  border: "1px solid color-mix(in srgb, var(--accent) 35%, transparent)",
  borderRadius: 7, padding: "1px 7px", whiteSpace: "nowrap",
};
// №22.5 (F10) — the free-text box. Dashed, like the preview block: what is inside it
// has not happened yet.
const otherWrap: CSSProperties = { paddingBottom: 8 };
/* crew `.ask` — `display:flex; gap:6px; margin-top:8px`. */
const askRow: CSSProperties = { display: "flex", gap: 6, marginTop: 8, alignItems: "flex-start" };
const otherInput: CSSProperties = {
  width: "100%", resize: "vertical", borderRadius: 7, padding: "7px 10px",
  border: "1px solid var(--border-strong)",
  background: "var(--surface-1)", color: "var(--text-1)",
  fontSize: 12.5, lineHeight: 1.5, fontFamily: "inherit", outline: "none",
};
// №22.5 (C3/C4/C1) — the commit row: Confirm · Skip · what a skip records · what's left.
const confirmRow: CSSProperties = {
  display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", paddingTop: 4, paddingBottom: 2,
};
const confirmBtn: CSSProperties = {
  border: "1px solid var(--accent)", background: "var(--accent)", color: "var(--accent-fg)",
  borderRadius: 7, padding: "7px 12px", fontSize: 12.5, fontWeight: 500, cursor: "pointer",
};
const skipBtn: CSSProperties = {
  border: "1px solid var(--border-strong)", background: "var(--surface-1)", color: "var(--text-1)",
  borderRadius: 7, padding: "7px 12px", fontSize: 12.5, fontWeight: 500, cursor: "pointer",
};
const skipNote: CSSProperties = { fontSize: 11.5, color: "var(--warn)", lineHeight: 1.4 };
const remainderNote: CSSProperties = { fontSize: 11.5, color: "var(--text-3)", marginLeft: "auto", whiteSpace: "nowrap" };
// №22.5 (C4) — the standing marks.
const skipMarksBlock: CSSProperties = { display: "flex", flexDirection: "column", gap: 3, marginTop: 8 };
const skipMarkLine: CSSProperties = { fontSize: 11.5, color: "var(--warn)", lineHeight: 1.45, marginTop: 6 };
/**
 * №53 (53.1.1) — THE OPTIONS ARE A COLUMN, NOT A WRAPPING ROW.
 *
 * This was `flex` + `flexWrap: "wrap"`, so the options laid themselves out by their
 * own text length: a long first option took the full width and two short ones then
 * sat SIDE BY SIDE beneath it. David's 2026-08-20 screenshot is exactly that —
 * "Reassign to a person" and "Skip with reason" as two columns under one full-width
 * row — and it reads as three unrelated controls rather than as one list of answers
 * to one question.
 *
 * The drawing has never been ambiguous about this: crew.css declares
 * `.opts { display: grid; gap: 7px }` (`docs/53-mockups/crew.css`), a column at every
 * width. Select mode already used the grid (`optRows` below, gap 7) — only direct
 * mode had drifted, which is why the same card could be a column or a broken grid
 * depending on a server-side commit-grammar stamp the reader cannot see.
 *
 * Options are ORDERED — the recommended one first, the escape hatch last — and an
 * order the eye has to reconstruct from a wrap is not an order.
 */
const opts: CSSProperties = { display: "grid", gap: 7, alignItems: "stretch", paddingBottom: 4 };
const optBase: CSSProperties = {
  borderRadius: 8, padding: "9px 11px", fontSize: 12.5, fontWeight: 400, cursor: "pointer",
  textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
};
/* №53 — crew `.opt` / `.opt.rec`. What these were: a SOLID ACCENT FILL for the
 * first option and an accent-bordered outline for the rest, so a two-option card
 * rendered as a dark teal slab above a teal outline. Crew tints exactly one row —
 * the recommended one — with a hairline accent edge over the palest teal, and
 * leaves every other row white on `--edge`. Nothing on the card is ever filled. */
const optPrimary: CSSProperties = { ...optBase, border: "1px solid var(--accent)", background: "var(--accent-tint)", color: "var(--text-1)" };
const optAlt: CSSProperties = { ...optBase, border: "1px solid var(--border-strong)", background: "var(--surface-1)", color: "var(--text-1)" };
const optConseq: CSSProperties = { fontWeight: 400, fontSize: 11.5, color: "var(--text-3)", marginLeft: 4 };
/* crew `.prep` — "ALREADY PREPARED": a recessed block on the card, not a dashed
 * warning. The dashed accent border said "this has not happened yet"; crew's block
 * says "this is already done, and here it is", which is the card's whole argument. */
const preview: CSSProperties = {
  margin: "0 0 12px", background: "var(--surface-2)", border: 0,
  borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--text-1)", lineHeight: 1.45,
};
const previewLabel: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-3)", fontWeight: 400, marginBottom: 5,
};
const foot: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", paddingTop: 8,
  fontSize: 12, color: "var(--text-3)",
};
const takeOverBtn: CSSProperties = {
  border: "none", background: "none", padding: 0, cursor: "pointer",
  fontSize: 12, fontWeight: 500, color: "var(--accent)",
};
// №22.3 (D-CH4) — a text button that reads as a link (the panel-focus affordance).
const linkButtonReset: CSSProperties = { border: 0, background: "none", padding: 0, cursor: "pointer", font: "inherit" };
const moreRow: CSSProperties = { paddingTop: 6, fontSize: 11.5, color: "var(--text-2)" };
const moreLink: CSSProperties = { color: "var(--accent)", fontWeight: 500, textDecoration: "none" };
const errLine: CSSProperties = { marginTop: 8, fontSize: 12, color: "var(--danger)" };

// ── №22.2 (D-CV1) — RESOLVED styles. Neutral (done), not warn (pending). ─────
const cardResolved: CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface-2)", borderRadius: 13,
  padding: "12px 15px 12px", maxWidth: 660,
};
const ageStamp: CSSProperties = { marginLeft: "auto", fontSize: 11, color: "var(--text-3)" };
const optChosen: CSSProperties = {
  ...optBase, cursor: "default",
  border: "1.5px solid var(--accent)", background: "color-mix(in srgb, var(--accent) 12%, var(--surface-1))",
  color: "var(--accent)",
};
const optChosenFailed: CSSProperties = {
  ...optBase, cursor: "default",
  border: "1.5px solid var(--danger)", background: "color-mix(in srgb, var(--danger) 12%, var(--surface-1))",
  color: "var(--danger)",
};
const optGreyed: CSSProperties = {
  ...optBase, cursor: "default",
  border: "1px solid var(--border)", background: "var(--surface-1)", color: "var(--text-3)", opacity: 0.65,
};
const stampRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", paddingTop: 9, marginTop: 2,
  borderTop: "0.5px solid var(--border)", fontSize: 12, color: "var(--text-2)",
};
// compact receipt — the one-liner (D-CV1).
const receiptRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, padding: "9px 13px", borderRadius: 11,
  border: "1px solid var(--border)", background: "var(--surface-2)", fontSize: 12.5,
};
const receiptCheck: CSSProperties = { fontWeight: 700, flex: "0 0 auto" };
const receiptText: CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const receiptLink: CSSProperties = { marginLeft: "auto", color: "var(--accent)", fontWeight: 600, textDecoration: "none", fontSize: 11.5, flex: "0 0 auto" };
